import Image from "next/image"
import { BRAND_ASSET_PATHS } from "@/lib/brand-assets"
import type { AppTile } from "@/data/comparisons"

/**
 * Artwork for the two sides of an app matchup.
 *
 * Our side renders the real What Grass Is This? app icon. The competitor side
 * renders a typeset wordmark plate instead: we have no licence to redistribute
 * another publisher's logo, and a generic phone glyph on both sides told the
 * reader nothing about which app they were looking at.
 *
 * `panel` is the hero VS split (4:3 media box); `thumb` is the /compare index
 * card (8:3 strip), where the wordmark is too small to set and the monogram
 * takes over.
 */
export function AppBrandTile({
  tile,
  name,
  variant,
}: {
  tile: AppTile
  name: string
  variant: "panel" | "thumb"
}) {
  const mark = tile.ours ? "ours" : "other"
  return (
    <div className={`cmp-apptile cmp-apptile--${mark} cmp-apptile--${variant}`} aria-hidden="true">
      {tile.ours ? (
        <div className="cmp-apptile__icon">
          <Image
            src={BRAND_ASSET_PATHS.iconDefault}
            alt=""
            fill
            sizes="(max-width: 640px) 96px, 120px"
          />
        </div>
      ) : (
        <div className="cmp-apptile__plate">
          {variant === "thumb" && tile.monogram ? (
            <span className="cmp-apptile__mono">{tile.monogram}</span>
          ) : (
            <span className="cmp-apptile__word">
              {(tile.lines ?? [name]).map((line) => (
                <span key={line}>{line}</span>
              ))}
            </span>
          )}
        </div>
      )}
    </div>
  )
}
